import { PopupType } from '@pp/core/src/components/popup/types';
import type {
  GameCommunicationMessageType,
  IGameCommunicationProcessor,
} from '@pp/core/src/controllers/gameCommunication/types';
import { Logger } from '@pp/core/src/logger/Logger';
import { FeatureService } from '@pp/core/src/services/features/FeatureService';
import { betActions } from '@pp/core/src/store/slices/bets/slice';
import { BetType } from '@pp/core/src/store/slices/bets/types';
import { gameStateActions } from '@pp/core/src/store/slices/game/slice';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { popupActions } from '@pp/core/src/store/slices/popup/slice';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';
import { countTotalBet } from '@pp/core/src/utils/bets';
import { roundNumber } from '@pp/core/src/utils/number';

import { BetIds, Bets } from '../../../config/bets';
import { GameStore } from '../../../store/GameStore';
import { autoCashoutActions } from '../../../store/slices/autocashout/slice';
import { betStepperActions } from '../../../store/slices/betStepper/slice';
import { crashAutoplayActions } from '../../../store/slices/crashAutoplay/slice';
import { highflyerStateActions } from '../../../store/slices/highflyerState/slice';
import { winStates } from '../../../store/slices/highflyerState/types';

type cashoutMsg = GameCommunicationMessageType & {
  bc: Bets;
  playerAmt: string;
  playerTotalAmt: string;
  bc1: string;
  bc2: string;
  jackpot: string;
};
export class GameComunicationCashOutProcessor implements IGameCommunicationProcessor {
  messageName = 'hf_cashOut';
  delay = 0;

  process(message: cashoutMsg, store: GameStore): void {
    const {
      gameState: { userInfo, currencyInfo },
    } = store.getState();
    const betCode = Number(message.bc);
    store.dispatch(highflyerStateActions.setCashedAmount({ betCode: betCode, amount: Number(message.playerAmt) }));
    store.dispatch(
      highflyerStateActions.setWinState(
        message.bc1 === 'false' && message.bc2 === 'false' ? winStates.youWin : winStates.cashed
      )
    );
    store.dispatch(highflyerStateActions.resetLocalCashedMul(betCode));
    setTimeout(() => {
      store.dispatch(highflyerStateActions.resetIsDealingInProgress(betCode));
      store.dispatch(betStepperActions.resetBetStack(betCode));
      store.dispatch(highflyerStateActions.resetIsBetPlaced(betCode));
      const {
        gameState,
        highflyerState: { balance },
        betStepper: { betInputValues },
      } = store.getState();
      this.checkAutoplayAvailability(store, Number(message.playerAmt), betCode);
      this.placeAutoplayBet(store, betCode);

      // //Update total bet with early bets when the current game is completely cashed out
      const isFullCashed = message.bc1 === 'false' && message.bc2 === 'false';
      if (isFullCashed) {
        store.dispatch(betActions.resetBetStack());
        store.dispatch(
          gameStateActions.updateBalance({
            ...store.getState().gameState?.userInfo?.balance,
            updateTimestamp: Date.now(),
          })
        );
        const { earlyBets, totalEarlyBetAmount } = this.getEarlyBets(store);
        store.dispatch(highflyerStateActions.setIsSingleBetPlaced(false));
        setTimeout(() => {
          if (![GameStatus.Result].includes(gameState.currentStatus as GameStatus)) {
            store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.WaitForNextGame));
          }
        }, 500);

        //Push the bet to stack only when early bets are preset
        if (totalEarlyBetAmount > 0) {
          store.dispatch(
            betActions.pushBet({
              bet: earlyBets,
              balance: (userInfo?.balance.amount || 0) + (userInfo?.balance.bonus || 0),
              decimals: currencyInfo?.digitsNumber || 2,
              freeChipValue: 0,
              freeChipBalance: 0,
            })
          );
        }
      }
      if (balance < Number(betInputValues[betCode])) {
        store.dispatch(
          betStepperActions.setBetInputValue({
            betId: betCode,
            value: roundNumber(balance, 2).toFixed(2),
            isBetFinalized: true,
          })
        );
        store.dispatch(betStepperActions.resetBetStack(betCode));
      }
    }, 2500);
  }
  protected getEarlyBets(store: GameStore): { earlyBets: BetType; totalEarlyBetAmount: number } {
    const { highflyerState } = store.getState();
    const earlyBets: BetType = {
      betSpots: {},
    };
    let totalEarlyBetAmount = 0;
    BetIds.forEach((betCode) => {
      earlyBets.betSpots = {
        ...earlyBets.betSpots,
        [betCode]: {
          value: highflyerState.earlyBetAmount[betCode],
        },
      };
      totalEarlyBetAmount += highflyerState.earlyBetAmount[betCode];
    });
    return {
      earlyBets,
      totalEarlyBetAmount,
    };
  }

  checkAutoplayAvailability(store: GameStore, win: number, betId: Bets): void {
    const state = store.getState();
    const { crashAutoplay, bets, config } = store.getState();
    const balance = state.gameState.userInfo?.balance;
    const crashAutoPlayInProgress = crashAutoplay.isInProgress[betId];
    const isAutoplayV2 = FeatureService.getFeature('isAutoplayV2') ?? false;
    if (crashAutoPlayInProgress) {
      const totalBet = countTotalBet(
        crashAutoplay.savedBet[betId]!.betType.betSpots || {},
        state.gameState.currencyInfo?.digitsNumber || 0
      );
      const savedBetSpots = (crashAutoplay.savedBet[betId]!.betType.betSpots?.[betId]?.value ?? 0) > 0;
      const allBetsUnavailable = savedBetSpots ? state.bets.disabledBetSpots.includes(betId.toString()) : null;
      this.stopAutoplay(betId, allBetsUnavailable, store, 'AUTOPLAY_PREFERENCES_STOPPED', 'BetsUnavailable');
      const autoPlayStop = totalBet > (balance?.amount || 0);
      this.stopAutoplay(betId, autoPlayStop, store, 'AUTOPLAY_SUPPORT_CURR_BAL', 'NoMoney');

      if (!crashAutoplay.isLaunchedMidRound[betId]) {
        const netChange = win - (bets.rawTotalBet ?? 0);
        store.dispatch(crashAutoplayActions.updateBalanceChange({ bestSpot: betId, count: netChange }));
      }
      if (crashAutoplay.roundsLeft[betId] <= 0 && !config.tableConfig?.isInfiniteAutoPlayEnabled) {
        store.dispatch(toastActions.createToast('AUTOPLAY_COMPLETED'));
        store.dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
        if (isAutoplayV2) {
          store.dispatch(crashAutoplayActions.openModal(betId));
        }
        Logger.logEvent('click', 'autoplay_stop', Date.now(), betId, 'Completed', false);
      }
      this.checkLimits(store, win, betId);
    }
  }

  checkLimits = (store: GameStore, win: number, betId: Bets): void => {
    const { crashAutoplay } = store.getState();
    if (crashAutoplay.isLaunchedMidRound[betId]) return;
    if (
      (crashAutoplay.singleWinLimit[betId] && crashAutoplay.singleWinLimit[betId] <= parseFloat(win.toString())) ||
      (crashAutoplay.balanceIncreaseLimit[betId] &&
        crashAutoplay.balanceIncreaseLimit[betId] <= crashAutoplay.balanceChange[betId]) ||
      (crashAutoplay.balanceDecreaseLimit[betId] &&
        -crashAutoplay.balanceDecreaseLimit[betId] >= crashAutoplay.balanceChange[betId])
    ) {
      store.dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
      store.dispatch(popupActions.pushPopup(PopupType.AutoplayStopped));
    }
  };

  protected stopAutoplay(betId: Bets, check: boolean | null, store: GameStore, msg: string, LoggerMsg: string): void {
    if (check) {
      store.dispatch(crashAutoplayActions.setInfiniteAutoplayEnabledOnCalncel({ bestSpot: betId, cancelled: false }));
      store.dispatch(crashAutoplayActions.stop({ betsSpot: Number(betId) }));
      store.dispatch(toastActions.createToast(msg));
      Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, LoggerMsg, false);
    }
  }

  protected placeAutoplayBet(store: GameStore, betId: Bets): void {
    const { crashAutoplay, bets } = store.getState();
    const crashAutoPlayInProgress = crashAutoplay.isInProgress[betId];
    if (crashAutoPlayInProgress) {
      //store.dispatch(betActions.clearBet(betId)); // only single bet should set accordingly the auto play kept bet id
      const disabledBets = bets.disabledBetSpots ?? [];
      const newBet: BetType = { betSpots: {} };
      if (
        crashAutoplay.savedBet[betId]!.betType.betSpots[betId].value > 0 &&
        !disabledBets.includes(betId.toString())
      ) {
        newBet.betSpots[betId] = crashAutoplay.savedBet[betId]!.betType.betSpots[betId];
      }
      if (newBet.betSpots[betId]) {
        store.dispatch(
          autoCashoutActions.setAutoCashoutButton({
            betCode: betId,
            isEnabled: crashAutoplay.savedBet[betId].isEnable,
          })
        );
        store.dispatch(
          autoCashoutActions.setCashoutMultiplier({
            betCode: betId,
            cashoutMultiplier: crashAutoplay.savedBet[betId].value,
          })
        );
        // check proper value
        store.dispatch(
          betStepperActions.setBetInputValue({
            betId: Number(betId),
            value: roundNumber(newBet.betSpots[betId].value, 2).toFixed(2),
          })
        );
        store.dispatch(
          highflyerStateActions.setEarlyBetAmount({
            betCode: betId,
            amount: newBet.betSpots[betId].value,
          })
        );
        store.dispatch(highflyerStateActions.setIsBetPlaced(betId));
      }
    }
  }
}
